import type { RecordDto, RecordKind } from '@halo/shared';
import { useMemo } from 'react';
import { parseChannel, type Channel } from '@/domain/records/channel';
import { parseCompetitor, type Competitor } from '@/domain/records/competitor';
import { parseMeetingReport, type MeetingReport } from '@/domain/records/meeting';
import {
  parseProject,
  parseProjectFolder,
  parseProjectImage,
  type Project,
  type ProjectFolder,
  type ProjectImage,
} from '@/domain/records/project';
import {
  parseKpiEvaluation,
  parseKpiStaff,
  parseKpiWeek,
  type KpiEvaluation,
  type KpiStaff,
  type KpiWeek,
} from '@/domain/kpi';
import { parseProjectWeek, type ProjectWeek } from '@/domain/project-inventory';
import { parseCompanySettings, type CompanySettings } from '@/domain/records/settings';
import { compareVideosOldestFirst, parseVideo, type Video } from '@/domain/records/video';
import { parseWorklogEntry, type WorklogEntry } from '@/domain/records/worklog';
import { parseWorkReport, type WorkReport } from '@/domain/work-report';
import { useWorkspace } from '@/stores/session-store';
import { useOwnerFilter } from './use-directory';

/** Bản ghi thô của một loại — đúng những gì máy chủ cho phép tài khoản này xem. */
const NO_RECORDS: RecordDto[] = [];

/**
 * Bản ghi thô của một loại. Thiếu loại thì trả mảng RỖNG dùng chung chứ không phải `undefined`:
 * thêm một loại bản ghi mới, trình duyệt nhận bản web mới trước khi máy chủ kịp khởi động lại là
 * `records.<loại mới>` chưa có — để nguyên thì cả app trắng màn hình (chốt 23/09/2026).
 */
export function useRawRecords(kind: RecordKind): RecordDto[] {
  return useWorkspace().records[kind] ?? NO_RECORDS;
}

/**
 * Parse CÓ NHỚ, dùng chung cho mọi màn hình. Store giữ nguyên tham chiếu của bản ghi không đổi và
 * chỉ thay mảng của loại vừa bị ghi, nên:
 *  - mỗi bản ghi chỉ parse lại khi CHÍNH NÓ đổi (sửa một ô không parse lại cả nghìn video);
 *  - nhiều nơi cùng đọc một loại (khung ứng dụng, màn hình, thẻ con) nhận CÙNG một mảng kết quả.
 * Kết quả vì thế là dữ liệu dùng chung — không nơi nào được sửa tại chỗ (muốn sắp xếp thì chép ra).
 */
function memoizedParser<T extends object>(parse: (record: RecordDto) => T) {
  const byRecord = new WeakMap<RecordDto, T>();
  const byList = new WeakMap<readonly RecordDto[], T[]>();
  return (records: readonly RecordDto[]): T[] => {
    let parsed = byList.get(records);
    if (!parsed) {
      parsed = records.map((record) => {
        let item = byRecord.get(record);
        if (!item) {
          item = parse(record);
          byRecord.set(record, item);
        }
        return item;
      });
      byList.set(records, parsed);
    }
    return parsed;
  };
}

const parseChannels = memoizedParser(parseChannel);
const parseCompetitors = memoizedParser(parseCompetitor);
const parseWorklog = memoizedParser(parseWorklogEntry);
const parseVideos = memoizedParser(parseVideo);
const parseProjects = memoizedParser(parseProject);
const parseProjectImages = memoizedParser(parseProjectImage);
const parseProjectFolders = memoizedParser(parseProjectFolder);
const parseProjectWeeks = memoizedParser(parseProjectWeek);
const parseKpiStaffList = memoizedParser(parseKpiStaff);
const parseKpiWeeks = memoizedParser(parseKpiWeek);
const parseKpiEvaluations = memoizedParser(parseKpiEvaluation);
const parseWorkReports = memoizedParser(parseWorkReport);

const sortedVideos = new WeakMap<readonly Video[], Video[]>();
/** Máy chủ trả theo thứ tự chèn; không sắp lại thì bảng tháng đổi thứ tự sau mỗi lần F5. */
function parseVideosOldestFirst(records: readonly RecordDto[]): Video[] {
  const videos = parseVideos(records);
  let sorted = sortedVideos.get(videos);
  if (!sorted) {
    sorted = [...videos].sort(compareVideosOldestFirst);
    sortedVideos.set(videos, sorted);
  }
  return sorted;
}

/**
 * Áp BỘ LỌC XEM (phòng → nhóm → người) lên một danh sách. Đây là lọc HIỂN THỊ, không phải phân
 * quyền — quyền đã được máy chủ áp từ trước khi dữ liệu tới đây.
 */
export function useOwnerFiltered<T extends { owner: string | null }>(items: readonly T[]): T[] {
  const passes = useOwnerFilter();
  return useMemo(() => items.filter((item) => passes(item.owner)), [items, passes]);
}

export const useVideos = (): Video[] => parseVideosOldestFirst(useRawRecords('video'));
/**
 * Kênh trong phạm vi nhìn. MẶC ĐỊNH BỎ kênh đã ẩn (26/09/2026): ẩn một kênh là muốn nó biến khỏi
 * mọi chỗ — danh sách, ô chọn dự án, màn SEO — chứ không phải chỉ một bảng. Màn nào cần soi lại
 * phần đã ẩn thì tự gọi với `includeHidden`.
 */
export const useChannels = (options?: { includeHidden?: boolean }): Channel[] => {
  const all = parseChannels(useRawRecords('channel'));
  const includeHidden = options?.includeHidden ?? false;
  return useMemo(
    () => (includeHidden ? all : all.filter((channel) => !channel.hidden)),
    [all, includeHidden],
  );
};
export const useCompetitors = (): Competitor[] => parseCompetitors(useRawRecords('competitor'));
export const useWorklog = (): WorklogEntry[] => parseWorklog(useRawRecords('worklog'));
/** Mọi dự án máy chủ gửi về — màn hình tự lọc theo `canSeeProject`. */
export const useProjects = (): Project[] => parseProjects(useRawRecords('project'));
export const useProjectImages = (): ProjectImage[] => parseProjectImages(useRawRecords('projimg'));
/** Folder trong dự án — ngăn chứa ảnh và file gốc của thumbnail. */
export const useProjectFolders = (): ProjectFolder[] =>
  parseProjectFolders(useRawRecords('projfd'));
/** Sản lượng theo tuần của mọi dự án (dùng chung như dự án). */
export const useProjectWeeks = (): ProjectWeek[] => parseProjectWeeks(useRawRecords('projwk'));
/** Biên bản họp tuần — dùng chung trong văn phòng, máy chủ đã lọc theo văn phòng. */
export const useMeetings = (): MeetingReport[] => useRawRecords('meeting').map(parseMeetingReport);
/** KPI: cấp bậc / hệ số của từng người và KPI tuần (dùng chung). */
export const useKpiStaff = (): KpiStaff[] => parseKpiStaffList(useRawRecords('kpistaff'));
export const useKpiWeeks = (): KpiWeek[] => parseKpiWeeks(useRawRecords('kpiweek'));
export const useKpiEvaluations = (): KpiEvaluation[] =>
  parseKpiEvaluations(useRawRecords('kpieval'));
/** Báo cáo công việc hằng ngày trong phạm vi nhìn. */
export const useWorkReports = (): WorkReport[] => parseWorkReports(useRawRecords('report'));

export function useCompanySettings(): CompanySettings {
  const [record] = useRawRecords('setting');
  return useMemo(() => parseCompanySettings(record), [record]);
}
